import React, { useEffect, useState, useCallback } from 'react';

/**
 * Global blog settings — shown identically on every post.
 * Stored as a singleton doc in Blog_DB.blog_settings.
 */
export default function BlogSettingsPanel({ API, adminToken, isDarkMode, showNotification }) {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${adminToken}` };

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${API}/blog/admin/settings`, { headers });
    if (res.ok) setS(await res.json());
    setLoading(false);
  }, [API, adminToken]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!s) return;
    setSaving(true);
    const res = await fetch(`${API}/blog/admin/settings`, {
      method: 'PUT',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(s),
    });
    setSaving(false);
    if (res.ok) showNotification?.('Settings saved', 'success');
    else showNotification?.('Save failed', 'error');
  };

  const set = (k, v) => setS({ ...s, [k]: v });
  const setCity = (i, k, v) => {
    const cities = [...(s.hero_cities || [])];
    cities[i] = { ...cities[i], [k]: v };
    setS({ ...s, hero_cities: cities });
  };
  const addCity = () => setS({ ...s, hero_cities: [...(s.hero_cities || []), { name: '', type: 'intl', word: 'Hello', script: '' }] });
  const removeCity = (i) => {
    const cities = [...(s.hero_cities || [])];
    cities.splice(i, 1);
    setS({ ...s, hero_cities: cities });
  };

  const muted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const inpBg = isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300';
  const border = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const cardBg = isDarkMode ? 'bg-gray-900/30' : 'bg-gray-50';

  if (loading || !s) return <div className={`text-center py-10 ${muted}`}>Loading…</div>;

  return (
    <div>
      <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold">Global blog settings</h2>
          <p className={`text-sm mt-0.5 ${muted}`}>Applied to every post and the list page. Change once, propagates everywhere.</p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold disabled:opacity-50"
        >
          {saving ? 'Saving…' : '💾 Save all'}
        </button>
      </div>

      {/* Master visibility toggle — saves IMMEDIATELY on click (no need for Save All) */}
      <VisibilityToggle
        s={s}
        setS={setS}
        API={API}
        headers={headers}
        muted={muted}
        showNotification={showNotification}
      />


      {/* ═════════════════════════════════════════════════════════════════
            Sections below are ordered TOP-TO-BOTTOM, matching how a visitor
            reads /blog. The only thing that ISN'T editable here is the
            light/dark mode toggle button — that's a behavioral control.
          ═════════════════════════════════════════════════════════════════ */}

      {/* 1. TOP NAV (the floating pill at the very top of every blog page) */}
      <div className={`p-5 rounded-lg border ${border} mb-5 ${cardBg}`}>
        <div className="text-xs uppercase tracking-wider font-bold mb-1 opacity-70">① Top nav (floating pill)</div>
        <p className={`text-xs mb-3 ${muted}`}>The pill at the very top of every blog page. The 🌙 dark-mode toggle next to the CTA is intentionally NOT editable — that's a behavioral control, not copy.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Brand icon (emoji/glyph next to brand name)" v={s.nav_brand_icon} onChange={v => set('nav_brand_icon', v)} inpBg={inpBg} />
          <Field label="Brand name" v={s.nav_brand_text} onChange={v => set('nav_brand_text', v)} inpBg={inpBg} />
          <Field label="Brand link URL (clicking brand goes here)" v={s.nav_brand_url} onChange={v => set('nav_brand_url', v)} inpBg={inpBg} />
          <div />
          <Field label="Home link label" v={s.nav_home_label} onChange={v => set('nav_home_label', v)} inpBg={inpBg} />
          <Field label="Home link URL" v={s.nav_home_url} onChange={v => set('nav_home_url', v)} inpBg={inpBg} />
          <Field label="Blog link label" v={s.nav_blog_label} onChange={v => set('nav_blog_label', v)} inpBg={inpBg} />
          <Field label="Blog link URL" v={s.nav_blog_url} onChange={v => set('nav_blog_url', v)} inpBg={inpBg} />
          <Field label="Mobile link label (e.g. '📱 Mobile')" v={s.nav_mobile_label} onChange={v => set('nav_mobile_label', v)} inpBg={inpBg} />
          <Field label="Mobile link URL (e.g. '/mobile')" v={s.nav_mobile_url} onChange={v => set('nav_mobile_url', v)} inpBg={inpBg} />
          <Field label="CTA pill label (e.g. 'Try DarijaPro →')" v={s.nav_cta_label} onChange={v => set('nav_cta_label', v)} inpBg={inpBg} />
          <Field label="CTA pill URL" v={s.nav_cta_url} onChange={v => set('nav_cta_url', v)} inpBg={inpBg} />
        </div>
      </div>

      {/* 2. HERO MAP — cities */}
      <div className={`p-5 rounded-lg border ${border} mb-5 ${cardBg}`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-wider font-bold opacity-70">② Hero animation cities (the map under the nav)</div>
            <p className={`text-xs mt-1 ${muted}`}>Airplane visits each in order, showing the local greeting at each stop.</p>
          </div>
          <button
            onClick={addCity}
            className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold"
          >
            + Add city
          </button>
        </div>
        <div className="grid grid-cols-12 gap-2 mb-2 text-xs font-bold opacity-70 px-2">
          <div className="col-span-3">City name</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-3">Greeting word</div>
          <div className="col-span-3">Arabic script (optional)</div>
          <div className="col-span-1"></div>
        </div>
        {(s.hero_cities || []).map((c, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center">
            <input
              value={c.name || ''}
              onChange={e => setCity(i, 'name', e.target.value)}
              className={`col-span-3 px-2 py-1.5 rounded border text-sm ${inpBg}`}
              placeholder="e.g. Casablanca"
            />
            <select
              value={c.type || 'intl'}
              onChange={e => setCity(i, 'type', e.target.value)}
              className={`col-span-2 px-2 py-1.5 rounded border text-sm ${inpBg}`}
            >
              <option value="intl">International</option>
              <option value="ma">Moroccan</option>
            </select>
            <input
              value={c.word || ''}
              onChange={e => setCity(i, 'word', e.target.value)}
              className={`col-span-3 px-2 py-1.5 rounded border text-sm ${inpBg}`}
              placeholder="e.g. Mar7ba / Bonjour"
            />
            <input
              value={c.script || ''}
              onChange={e => setCity(i, 'script', e.target.value)}
              className={`col-span-3 px-2 py-1.5 rounded border text-sm ${inpBg}`}
              placeholder="e.g. مرحبا (optional)"
              dir="rtl"
            />
            <button
              onClick={() => removeCity(i)}
              className="col-span-1 text-red-500 hover:text-red-400 text-sm"
              title="Remove city"
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      {/* 3. MASTHEAD (under the map) */}
      <div className={`p-5 rounded-lg border ${border} mb-5 ${cardBg}`}>
        <div className="text-xs uppercase tracking-wider font-bold mb-3 opacity-70">③ Masthead (the big title block under the map)</div>
        <Field label="Eyebrow (small line above the big title, e.g. 'In-flight reading · Darija for travelers')" v={s.brand_subtitle} onChange={v => set('brand_subtitle', v)} inpBg={inpBg} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Big title — part 1 (left, dark)" v={s.brand_title_part1} onChange={v => set('brand_title_part1', v)} inpBg={inpBg} />
          <Field label="Big title — part 2 (right, accent color)" v={s.brand_title_part2} onChange={v => set('brand_title_part2', v)} inpBg={inpBg} />
        </div>
        <Field label="Subtitle (one line under the big title)" v={s.list_page_sub} onChange={v => set('list_page_sub', v)} inpBg={inpBg} multiline />
        <Field label="Disclaimer pill (small italic note. Leave blank to hide.)" v={s.disclaimer} onChange={v => set('disclaimer', v)} inpBg={inpBg} multiline />
      </div>

      {/* 4. DESTINATIONS section (categories cards block) */}
      <div className={`p-5 rounded-lg border ${border} mb-5 ${cardBg}`}>
        <div className="text-xs uppercase tracking-wider font-bold mb-1 opacity-70">④ Destinations section (the categories grid)</div>
        <p className={`text-xs mb-3 ${muted}`}>To edit the actual cards (icon / name / description), go to the <b>Categories</b> tab.</p>
        <Field label="Eyebrow above the heading (e.g. 'Destinations')" v={s.destinations_label} onChange={v => set('destinations_label', v)} inpBg={inpBg} />
        <Field label="Heading (e.g. 'Choose your flight')" v={s.list_page_heading} onChange={v => set('list_page_heading', v)} inpBg={inpBg} />
        <Field label="Curator line (shown after the category count, e.g. '· curated by DarijaPro'. Blank to hide.)" v={s.destinations_curator_line} onChange={v => set('destinations_curator_line', v)} inpBg={inpBg} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <Field label="Card post-count word — singular (1 ___)" v={s.cat_card_flight_singular} onChange={v => set('cat_card_flight_singular', v)} inpBg={inpBg} />
          <Field label="Card post-count word — plural (3 ___)" v={s.cat_card_flight_plural} onChange={v => set('cat_card_flight_plural', v)} inpBg={inpBg} />
          <Field label="Card empty label (0 · ___)" v={s.cat_card_empty_label} onChange={v => set('cat_card_empty_label', v)} inpBg={inpBg} />
        </div>
      </div>

      {/* 5. EMPTY STATE (shown when no posts yet) */}
      <div className={`p-5 rounded-lg border ${border} mb-5 ${cardBg}`}>
        <div className="text-xs uppercase tracking-wider font-bold mb-1 opacity-70">⑤ Empty state (only visible when no published posts)</div>
        <p className={`text-xs mb-3 ${muted}`}>Shows in place of the ticket grid until at least one post is published.</p>
        <Field label="Icon (emoji or glyph above the title)" v={s.empty_state_icon} onChange={v => set('empty_state_icon', v)} inpBg={inpBg} />
        <Field label="Title (e.g. 'No flights scheduled yet.')" v={s.empty_state_title} onChange={v => set('empty_state_title', v)} inpBg={inpBg} />
        <Field label="Body (e.g. 'Check back soon — the first posts are coming.')" v={s.empty_state_body} onChange={v => set('empty_state_body', v)} inpBg={inpBg} multiline />
      </div>

      {/* 6. TICKET CARDS (per-post grid + post-page boarding pass) */}
      <div className={`p-5 rounded-lg border ${border} mb-5 ${cardBg}`}>
        <div className="text-xs uppercase tracking-wider font-bold mb-1 opacity-70">⑥ Ticket cards (and the post page boarding pass)</div>
        <p className={`text-xs mb-3 ${muted}`}>Shared copy across every ticket on the home grid and every post page header. Per-post data like flight number, route, seat, gate is set inside each post.</p>
        <Field label="Airline name on the ★ header strip (e.g. 'DARIJAPRO JOURNEYS')" v={s.airline_name} onChange={v => set('airline_name', v)} inpBg={inpBg} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="'From' label (route)" v={s.ticket_from_label} onChange={v => set('ticket_from_label', v)} inpBg={inpBg} />
          <Field label="'To' label (route)" v={s.ticket_to_label} onChange={v => set('ticket_to_label', v)} inpBg={inpBg} />
          <Field label="'Seat' label (stub)" v={s.ticket_seat_label} onChange={v => set('ticket_seat_label', v)} inpBg={inpBg} />
          <Field label="'PHRASES' word in meta (e.g. '10 PHRASES')" v={s.ticket_phrases_label} onChange={v => set('ticket_phrases_label', v)} inpBg={inpBg} />
          <Field label="'DRAFT' label (shown in meta when not yet published)" v={s.ticket_draft_label} onChange={v => set('ticket_draft_label', v)} inpBg={inpBg} />
        </div>
      </div>

      {/* 7. FOOTER */}
      <div className={`p-5 rounded-lg border ${border} mb-5 ${cardBg}`}>
        <div className="text-xs uppercase tracking-wider font-bold mb-3 opacity-70">⑦ Footer</div>
        <Field label="Farewell line (big saffron handwritten line, e.g. '— with love from Morocco —')" v={s.footer_farewell} onChange={v => set('footer_farewell', v)} inpBg={inpBg} />
        <Field label="Copyright line (e.g. '© 2026 DarijaPro · The Journal')" v={s.footer_copyright} onChange={v => set('footer_copyright', v)} inpBg={inpBg} />
      </div>

      {/* 8. PASSPORT WIDGET (floating bottom-right stamp counter) */}
      <div className={`p-5 rounded-lg border ${border} mb-5 ${cardBg}`}>
        <div className="text-xs uppercase tracking-wider font-bold mb-1 opacity-70">⑧ Passport widget (the floating bottom-right stamp counter)</div>
        <p className={`text-xs mb-3 ${muted}`}>Tracks unique posts a visitor has visited (stored in their browser localStorage). Increments when they open a post.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Top label (e.g. 'PASSPORT')" v={s.passport_label} onChange={v => set('passport_label', v)} inpBg={inpBg} />
          <Field label="Stamp word — singular ('1 STAMP')" v={s.passport_stamps_singular} onChange={v => set('passport_stamps_singular', v)} inpBg={inpBg} />
          <Field label="Stamp word — plural ('3 STAMPS')" v={s.passport_stamps_plural} onChange={v => set('passport_stamps_plural', v)} inpBg={inpBg} />
        </div>
      </div>

      {/* Extra: mascot — used by post page callouts as a default; not visible on home */}
      <div className={`p-5 rounded-lg border ${border} mb-5 ${cardBg}`}>
        <div className="text-xs uppercase tracking-wider font-bold mb-3 opacity-70">Misc — used on post pages</div>
        <Field label="Default mascot image URL (used on post-page callouts when a post doesn't override it)" v={s.mascot_character} onChange={v => set('mascot_character', v)} inpBg={inpBg} />
      </div>

      {/* 📢 Announcement bar — orange strip at the very top of every blog page */}
      <div className={`p-5 rounded-lg border-2 mb-5 ${s.announcement_enabled ? 'border-orange-500/60 bg-orange-500/5' : border + ' ' + cardBg}`}>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div>
            <div className="text-xs uppercase tracking-wider font-bold opacity-80">📢 Announcement bar</div>
            <p className={`text-xs mt-1 ${muted}`}>Orange strip at the very top of every blog page. Toggle on to publish.</p>
          </div>
          <label className="flex items-center gap-2 text-sm font-bold cursor-pointer">
            <input
              type="checkbox"
              checked={!!s.announcement_enabled}
              onChange={e => set('announcement_enabled', e.target.checked)}
              className="w-5 h-5"
            />
            <span className={s.announcement_enabled ? 'text-orange-500' : muted}>
              {s.announcement_enabled ? 'ENABLED — visible to all visitors' : 'Hidden'}
            </span>
          </label>
        </div>
        <Field
          label="Announcement text (e.g. 'New post: how to bargain in the souk')"
          v={s.announcement_text}
          onChange={v => set('announcement_text', v)}
          inpBg={inpBg}
          multiline
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field
            label="Optional link label (e.g. 'Read now')"
            v={s.announcement_link_label}
            onChange={v => set('announcement_link_label', v)}
            inpBg={inpBg}
          />
          <Field
            label="Optional link URL (e.g. /blog/bargain-like-a-local-10-darija-souk-phrases)"
            v={s.announcement_link_url}
            onChange={v => set('announcement_link_url', v)}
            inpBg={inpBg}
          />
        </div>
        <p className={`text-xs mt-2 ${muted} italic`}>
          The bar renders only when the toggle is ON <em>and</em> the text is non-empty. Click 💾 Save all above to apply.
        </p>
      </div>
    </div>
  );
}

function VisibilityToggle({ s, setS, API, headers, muted, showNotification }) {
  // Treat any value that isn't strictly `false` as visible (default-on if field is missing)
  const isVisible = s.is_visible !== false;
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    const next = !isVisible;
    setBusy(true);
    // Optimistic local update
    setS({ ...s, is_visible: next });
    // Save IMMEDIATELY to backend so /blog reflects the change without clicking Save All
    try {
      const res = await fetch(`${API}/blog/admin/settings`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...s, is_visible: next }),
      });
      if (res.ok) {
        showNotification?.(next ? 'Blog is now VISIBLE to the public' : 'Blog is now HIDDEN — visitors redirected to home', 'success');
      } else {
        // rollback on failure
        setS({ ...s, is_visible: !next });
        showNotification?.('Toggle failed — try again', 'error');
      }
    } catch (e) {
      setS({ ...s, is_visible: !next });
      showNotification?.(`Toggle error: ${e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`p-5 rounded-lg border-2 mb-5 ${isVisible ? 'border-green-500/50 bg-green-500/5' : 'border-red-500/50 bg-red-500/10'}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <div className={`text-3xl ${isVisible ? '' : 'opacity-60'}`}>
            {isVisible ? '✈' : '🚫'}
          </div>
          <div>
            <div className="text-sm font-bold uppercase tracking-wider opacity-80">Blog visibility</div>
            <div className="text-lg font-extrabold mt-0.5">
              {isVisible
                ? <span className="text-green-500">VISIBLE — public can read all posts</span>
                : <span className="text-red-500">HIDDEN — visitors redirected to home; "Blog" link auto-removed from nav</span>}
            </div>
            <p className={`text-xs mt-1 ${muted}`}>
              Admin live-preview keeps working even when hidden, so you can keep editing privately.
            </p>
          </div>
        </div>
        <button
          onClick={toggle}
          disabled={busy}
          className={`px-5 py-3 rounded-lg text-sm font-extrabold uppercase tracking-wider disabled:opacity-50 ${
            isVisible
              ? 'bg-red-600 hover:bg-red-700 text-white'
              : 'bg-green-600 hover:bg-green-700 text-white'
          }`}
        >
          {busy ? 'Saving…' : (isVisible ? '🚫 Hide blog' : '✓ Show blog')}
        </button>
      </div>
      <p className={`text-xs mt-3 ${muted} italic`}>
        Toggle saves instantly — no need to click Save All for this switch.
      </p>
    </div>
  );
}

function Field({ label, v, onChange, inpBg, multiline }) {
  return (
    <div className="mb-3">
      <label className="text-xs font-semibold opacity-80 mb-1 block">{label}</label>
      {multiline ? (
        <textarea
          value={v || ''}
          onChange={e => onChange(e.target.value)}
          rows={2}
          className={`w-full px-3 py-2 rounded border text-sm ${inpBg}`}
        />
      ) : (
        <input
          value={v || ''}
          onChange={e => onChange(e.target.value)}
          className={`w-full px-3 py-2 rounded border text-sm ${inpBg}`}
        />
      )}
    </div>
  );
}
